import * as React from "react";
import axios from "axios";
import {flashError} from "components/utils/Flash";
import Table from "react-bootstrap/Table";
import {Component, Item} from "components/models/Quotation";

interface Props {
  quotationId: string;
  customerId: string;
  itemId: string;
  costMode: boolean;
}

interface State {
  loading: boolean;
  item?: Item;
  editId?: string;
  editPrice?: string;
  updating: boolean;
}

export default class extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      loading: true,
      updating: false,
    }
  }

  componentDidMount() {
    this.loadItem();
  }

  loadItem() {
    axios.get(`/customers/${this.props.customerId}/quotations/${this.props.quotationId}/items/${this.props.itemId}.json`)
      .then((resp) => {
        this.setState({editId: undefined, editPrice: undefined, item: resp.data as Item, loading: false, updating: false})
      })
      .catch(() => {
        flashError('An issue occurred loading the item', 'Error');
      })
  }

  bulkIcon(bulk: boolean) {
    if (bulk) {
      return <i className={"fas fa-fw fa-check text-dark"}/>
    }

    return <i className={"fas fa-fw fa-times text-muted"}/>
  }

  row(comp: Component) {
    const id = comp.id;
    const editing = id === this.state.editId;
    return <tr key={id}>
      <td>
        <a
          href={`/customers/${this.props.customerId}/quotations/${this.props.quotationId}/items/${this.props.itemId}/components/${id}/edit`}>
          {comp.number.toString().padStart(6, "0")}
        </a>
      </td>
      <td>
        <a
          target={"_blank"}
          href={`/products/${comp.product._id['$oid']}`}>
          {comp.product_description}
        </a></td>
      <td className={"text-right"}>{comp.quantity}</td>
      <td className={"text-right"}>{comp.length.micrometres !== 0 && comp.length_distance}</td>
      <td className={"text-right"}>{comp.width.micrometres !== 0 && comp.width_distance}</td>
      <td className={"text-right"}>{(comp.length.micrometres + comp.width.micrometres  !== 0) ? (`${comp.square_metre} m2`) : ""}</td>
      <td className={"text-center"}>{this.bulkIcon(comp.bulk_purchase)}</td>
      {this.props.costMode && <td className="text-right">{this.compPrice(comp, comp.product_cost.cents)}</td>}
      <td className={"text-right component-price-r" + (editing ? " editing" : "")}
          onClick={() => {
            if (editing || this.state.updating) {
              return;
            }

            this.setState({editId: id, editPrice: (comp.product_price.cents / 100).toString()}, () => {
              const edit = document.getElementById("editPrice");
              if (!edit) {
                return;
              }
              edit.focus();
              (edit as HTMLInputElement).select();
            })
          }}>
        {this.compPriceElem(editing, comp)}
      </td>
      <td className={"text-right"}>
        <a className={"btn btn-sm btn-light"}
           href={`/customers/${this.props.customerId}/quotations/${this.props.quotationId}/items/${this.props.itemId}/components/${id}/edit`}>
          <i className={"fas fa-edit mr-2"}/>
          Edit
        </a>
      </td>
    </tr>
  }

  rows(item: Item) {
    if (item.components.length === 0) {
      return (<tr><td colSpan={this.colCount()} className={"text-center"}><a href={`/customers/${this.props.customerId}/quotations/${this.props.quotationId}/items/${this.props.itemId}/components/new`}>Add a component</a></td></tr>);
    }
    return item.components.map((c) => this.row(c));
  }

  table() {
    const item = this.state.item!;
    return <Table className="padded no-top-border">
      <thead>
      <tr>
        <th className="w10px">Number</th>
        <th>Product</th>
        <th className="text-right w5p">Quantity</th>
        <th className="text-right w7p">Length</th>
        <th className="text-right w7p">Width</th>
        <th className="text-right w7p">Area</th>
        <th className="w5p">Bulk?</th>
        {this.props.costMode && <th className="text-right w100px">Cost</th>}
        <th className="text-right w100px">Retail</th>
        <th className="w5p"></th>
      </tr>
      </thead>
      <tbody>
      {this.rows(item)}
      </tbody>
    </Table>
  }

  colCount() {
    return (this.props.costMode ? 10 : 9);
  }

  loading() {
    return (
      <div className="text-center my-3">
        <i className="fas fa-spinner fa-pulse fa-2x"></i>
      </div>
    )
  }

  render() {
    return (
      <>
        {this.state.loading ? this.loading() : this.table()}
      </>
    )
  }

  private compPrice(comp: Component, price = comp.product_price.cents) {
    return <span>{Intl.NumberFormat('en-GB', {
      style: "currency",
      currency: comp.product_price.currency_iso
    }).format(price / 100)}</span>
  }

  private editPrice(comp: Component) {
    return (
      <input type="" id="editPrice" value={this.state.editPrice}
             onKeyPress={(e) => {
               if (e.key === 'Enter') {
                 this.finishEdit(comp);
               }
             }}
             onBlur={() => this.finishEdit(comp)}
             onChange={(e) => this.setState({editPrice: e.target.value})}/>
    )
  }

  private finishEdit(comp: Component) {
    {
      const val = parseFloat(this.state.editPrice!);
      if (isNaN(val) || (val * 100) === comp.product_price.cents) {
        this.setState({editPrice: undefined, editId: undefined})
        return;
      }
      this.setState({updating: true}, () => {
        axios.post(`/customers/${this.props.customerId}/quotations/${this.props.quotationId}/items/${this.props.itemId}/components/${comp.id}/update_price.json`, {
          quotation_item_component: {product_price: val},
          authenticity_token: (window as any)._token
        })
          .then((resp) => this.loadItem())
          .catch(() => {
            flashError('An issue occurred loading the item', 'Error');
          })
      })
    }
  }

  private compPriceElem(editing: boolean, comp: Component) {
    if (this.state.updating && editing) {
      return "..."
    }
    return editing ? this.editPrice(comp) : this.compPrice(comp)
  }

}
